import { useDispatch } from 'react-redux'
import { t } from 'ttag'
import styled, { css } from 'styled-components'
import PropTypes from 'prop-types'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useContext, useState } from 'react'

import {
  NetworkIcon,
  ChatIcon,
  CareerHubIcon,
  CheckCircleIcon,
  TimesCircleIcon,
  LoginIcon,
  RecommendationIcon,
  NotesIcon
} from '@h2/components/icons'
import { openSigninDialog } from '@main/actions/signinOrRegisterDialog'
import { Button } from '@h2/components/buttons/button'
import { BLACK, WHITE, SO60S, PB97T, SLB90T, PB90T, SLB40T } from '@h2/components/colors'
import { Heading } from '@h2/components/typography/heading'
import { Text } from '@h2/components/typography/text'
import { Link } from '@h2/common/components/link'
import { api } from '@h2/api'
import { Skeleton } from '@h2/components/loading/skeleton'
import { Flex } from '@h2/components/layout/flex'
import { showUserFeedback } from '@h2/common/actions/showUserFeedback'
import { Modal } from '@h2/components/containers/modal'

import { JournalPopup } from './journalPopup'
import { LegacyNextSteps } from './legacyNextSteps'

import { findThumbnail } from '../utils/thumbnailLookup'
import { Globals } from '../../../globals'

const MoreItemsContainer = styled(Flex)`
  padding: 0 5px;
`
const RecommendationDetails = styled.div`
  border: 1px solid ${PB90T};
  background-color: ${PB97T};
  padding: 5px;
  margin-bottom: 5px;
`
const NextStepBox = styled.div`
  background-color: ${WHITE};
  width: 100%;
  text-align: center;
  padding: 15px;
`
const JournalBox = styled(NextStepBox)`
  background-color: ${PB90T};
  margin-top: 5px;
  padding: 5px;
  text-align: right;
`
const SidebarTitle = styled(Heading)`
  font-size: 17px;
  margin-top: 20px;
`
const WhiteCircle = styled.div`
  border-radius: 50%;
  display: inline-block;
  background-color: white;
  width: 30px;
  height: 30px;
  text-align: center;
  line-height: 40px;
  float: left;
  margin-right: 10px;
  margin-bottom: 5px;
`
const RecommendationCard = styled.div`
  border-radius: 5px;
  background-color: white;
  min-height: 150px;
  width: 100%;
  margin-top: 5px;
  margin-bottom: 15px;
  padding: 10px;
  ${({ isPromotion }) =>
    isPromotion
      ? css`
          position: relative;
          border-top: 30px solid ${SLB90T};
          border-top-right-radius: 7px;
          border-top-left-radius: 7px;
          margin-bottom: 3px;
        `
      : ''};
`
const PromotionContainer = styled.div`
  display: flex;
  position: absolute;
  top: -25px;
  right: 15px;
  font-weight: bold;
  color: ${SLB40T};
  align-items: center;
`
const InlineBlock = styled.div`
  display: inline-block;
`
const ThumbnailImage = styled.img`
  max-width: 100%;
`
const Badge = styled.div`
  padding: 4px;
  background-color: ${PB90T};
  display: inline-block;
  margin-left: 3px;
  border-radius: 5px;
`
const CardContent = ({
  item,
  nextStepUrl,
  resultCount,
  isPromotion,
  showNoResults = true
}) => {
  if (!item || item.length === 0) {
    return showNoResults ? (
      <div className="clear">
        <RecommendationCard>
          <Text color={SO60S} size="medium" align="center">
            {t`There are no related items to show at this time`}
          </Text>
        </RecommendationCard>
      </div>
    ) : null
  }

  const resultCountFormatted = resultCount?.toLocaleString() ?? 'n/a'
  const showMore = resultCount > item.length

  return (
    <div className="clear">
      {item.map(({ url, name, flags, description, providedId }) => {
        const offeringThumbail = findThumbnail(providedId)

        return (
          <Link href={url} key={url} align="left">
            <RecommendationCard isPromotion={isPromotion}>
              {isPromotion && (
                <PromotionContainer>
                  <RecommendationIcon size="small" />
                  {t`Promoted`}
                </PromotionContainer>
              )}
              <Text
                size={isPromotion ? 'large' : 'medium'}
                weight="bold"
                margin="0 0 5px 0">
                {name}
              </Text>
              {offeringThumbail ? (
                isPromotion ? (
                  <>
                    <ThumbnailImage
                      style={{ float: 'right', width: '50%' }}
                      src={offeringThumbail}
                      alt={name}
                      aria-hidden={true}
                    />
                    <Text size="medium">{description.substring(0, 250)}</Text>
                  </>
                ) : (
                  <ThumbnailImage src={offeringThumbail} alt={name} aria-hidden={true} />
                )
              ) : (
                <Text size="medium">{description.substring(0, 250)}</Text>
              )}
              <div className="result-flags search-filter-bar">
                {flags?.map(flag => (
                  <InlineBlock
                    className={`expandable-button small ${flag.cssClassName}`}
                    key={`sri--inner-${flag.flag}`}>
                    <span className="icon"></span>
                    <div>{flag.flag}</div>
                  </InlineBlock>
                ))}
              </div>
            </RecommendationCard>
          </Link>
        )
      })}
      {showMore && (
        <MoreItemsContainer>
          <Text style={{ width: '100%' }} as="span" color={WHITE} size="small">
            {t`1 of ${resultCountFormatted}`}
          </Text>
          {nextStepUrl && (
            <Flex style={{ width: '100%' }} justifyContent="right" direction="row">
              <Link
                href={nextStepUrl}
                variant="anchor"
                display="monochrome"
                align="right"
                style={{ justifyContent: 'right' }}>
                <Text weight="bold" as="span" color={WHITE}>
                  {t`View all`}
                </Text>
              </Link>
            </Flex>
          )}
        </MoreItemsContainer>
      )}
    </div>
  )
}
const NextSteps = ({ user, recommendations, objectId, objectType }) => {
  const dispatch = useDispatch()
  const { tenantId } = useContext(Globals)
  const client = useQueryClient()
  const [journalModalOpen, setJournalModalOpen] = useState(false)
  const queryKey = [`flag`, objectId, objectType]
  const { data: flaggedItemData, isFetching } = useQuery(
    queryKey,
    () => {
      return api.fetch(`/flagged/${objectType}/${objectId}`, { method: 'GET' })
    },
    { enabled: Boolean(user) }
  )
  const { mutate, isLoading } = useMutation(
    body =>
      api.fetch(`/flagged/${objectType}/${objectId}`, {
        method: flaggedItemData?.isSaved ? 'DELETE' : 'PUT',
        body: JSON.stringify(body)
      }),
    {
      onSuccess: result => {
        client.setQueryData(queryKey, () => {
          return result
        })
      },
      onError: () => {
        dispatch(
          showUserFeedback(
            t`An error occurred while attempting to add this to your CareerHub`,
            'error'
          )
        )
      }
    }
  )
  const { mutate: mutateAcceptance, isLoading: isAccepting } = useMutation(
    () => {
      return api.fetch(`/recommendations/accept/${objectType}/${objectId}`, {
        method: 'POST',
        body: {}
      })
    },
    {
      onSuccess: async () => {
        await client.refetchQueries([`flag`, objectId, objectType], {
          type: 'all',
          exact: true
        })
      },
      onError: () => {
        dispatch(
          showUserFeedback(
            t`An error occurred while attempting to accept recommendation`,
            'error'
          )
        )
      }
    }
  )
  const { mutate: mutateRejection, isLoading: isRejecting } = useMutation(
    () => {
      return api.fetch(`/recommendations/reject/${objectType}/${objectId}`, {
        method: 'POST',
        body: {}
      })
    },
    {
      onSuccess: async () => {
        await client.refetchQueries([`flag`, objectId, objectType], {
          type: 'all',
          exact: true
        })
      },
      onError: () => {
        dispatch(
          showUserFeedback(
            t`An error occurred while attempting to reject recommendation`,
            'error'
          )
        )
      }
    }
  )
  const nextStepContent = () => {
    //if the id wasn't specified, we don't need to render
    if (!objectId || !objectType) {
      return null
    }

    if (!user) {
      return (
        <NextStepBox>
          <Heading level="3">{t`Next Step`}</Heading>
          <Text size="medium" color={BLACK}>{t`You must sign in to take action`}</Text>
          <Button
            align="right"
            display="promo"
            icon={LoginIcon}
            onClick={() => dispatch(openSigninDialog('signIn'))}>
            {t`Sign In`}
          </Button>
        </NextStepBox>
      )
    }

    if (isFetching) {
      return <Skeleton />
    }

    const buttonContent = () => {
      const navRec = flaggedItemData?.navigatorRecommendation

      if (navRec && !(navRec.deleted || navRec.accepted)) {
        return (
          <>
            {navRec.history.map(itm => (
              <RecommendationDetails key={itm.id}>
                <Heading level="5" removeMargins={true}>
                  {t`Recommended by: ${itm.recommendedBy.name}`}
                </Heading>
                <Flex>
                  <ChatIcon size="medium" />
                  <Text size="medium" margin="5px 0 0 2px">
                    {itm.comments}
                  </Text>
                </Flex>
              </RecommendationDetails>
            ))}
            <Flex gap="5px">
              <Button
                display="red"
                isFullWidth={true}
                iconAtTop={true}
                type="button"
                isLoading={isRejecting}
                isDisabled={isAccepting || isRejecting}
                onClick={mutateRejection}
                icon={TimesCircleIcon}>
                {t`Decline`}
              </Button>
              <Button
                isFullWidth={true}
                iconAtTop={true}
                type="button"
                isLoading={isAccepting}
                isDisabled={isAccepting || isRejecting}
                onClick={mutateAcceptance}
                icon={CheckCircleIcon}>
                {t`Accept`}
              </Button>
            </Flex>
          </>
        )
      }

      return (
        <Button
          icon={CareerHubIcon}
          align="right"
          isFullWidth={true}
          iconAtTop={true}
          display={flaggedItemData?.isSaved ? 'red' : 'default'}
          isLoading={isLoading}
          onClick={() => mutate({})}>
          {flaggedItemData?.isSaved ? t`Remove from CareerHub` : t`Add to CareerHub`}
        </Button>
      )
    }

    return (
      <NextStepBox>
        <Heading level="3">{t`Next Step`}</Heading>
        {buttonContent()}
      </NextStepBox>
    )
  }
  const onSaveJournal = newJournal => {
    client.setQueryData(queryKey, oldData => {
      if (!newJournal) {
        return oldData
      }

      const { entry } = newJournal

      return {
        ...oldData,
        journals: {
          ...oldData.journals,
          entries: [...oldData.journals.entries, entry]
        }
      }
    })
  }
  const journalContent = () => {
    if (!user) {
      return null
    }

    const title = flaggedItemData?.title ?? t`Unknown`

    return (
      <JournalBox>
        <Button
          align="right"
          variant="outlined"
          size="small"
          icon={NotesIcon}
          onClick={() => setJournalModalOpen(true)}>
          {t`View Journals`}
          <Badge>{flaggedItemData?.journals.entries.length ?? 0}</Badge>
        </Button>
        <Modal
          isOpen={journalModalOpen}
          closeOnClickOverlay={true}
          onClose={() => setJournalModalOpen(false)}>
          <Modal.Header>
            <Heading level="3">{t`Journals for ${title}`}</Heading>
          </Modal.Header>
          <Modal.Body>
            <div style={{ maxHeight: '80vh', overflow: 'scroll' }}>
              <JournalPopup
                journal={flaggedItemData?.journals}
                objectType={objectType}
                objectId={objectId}
                onComplete={onSaveJournal}
                title={flaggedItemData?.title}
              />
            </div>
          </Modal.Body>
        </Modal>
      </JournalBox>
    )
  }
  const contentOrLoader = sectionKey => {
    if (!recommendations) {
      return <Skeleton width="100%" height="100px" />
    }

    const lookupNextStep = () => {
      switch (sectionKey) {
        case 'educationTraining':
          return {
            link: recommendations.moreEducationLink,
            count: recommendations.totalEducationLink,
            isPromotion: false
          }
        case 'institutions':
          return {
            link: recommendations.moreInstitutionsLink,
            count: recommendations.totalInstitutionsLink,
            isPromotion: false
          }
        case 'jobOpenings':
          return {
            link: recommendations.moreJobOpeningsLink,
            count: recommendations.totalJobsLink,
            isPromotion: false
          }
        case 'occupations':
          return {
            link: recommendations.moreOccupationsLink,
            count: recommendations.totalOccupationsLink,
            isPromotion: false
          }
        case 'otherOpportunities':
          return {
            link: recommendations.moreOpportunitiesLink,
            count: recommendations.totalOpportunitiesLink,
            isPromotion: false
          }
        case 'promotions':
          return {
            link: '',
            count: 1,
            isPromotion: true
          }
        default:
          return null
      }
    }
    const next = lookupNextStep()

    return (
      <CardContent
        item={recommendations[sectionKey]}
        nextStepUrl={next.link}
        resultCount={next.count}
        showNoResults={!next.isPromotion}
        isPromotion={next.isPromotion}
      />
    )
  }

  if (tenantId !== 'nevada') {
    return (
      <LegacyNextSteps
        objectId={objectId}
        objectType={objectType}
        user={user}
        recommendations={recommendations}
      />
    )
  }

  return (
    <>
      {nextStepContent()}
      {journalContent()}
      <br />
      {contentOrLoader('promotions')}
      <SidebarTitle level="3">
        <WhiteCircle>
          <NetworkIcon />
        </WhiteCircle>{' '}
        {t`Continue Exploration`}
      </SidebarTitle>
      {contentOrLoader('otherOpportunities')}
    </>
  )
}

NextSteps.propTypes = {
  recommendations: PropTypes.object.isRequired,
  user: PropTypes.object,
  objectId: PropTypes.string,
  objectType: PropTypes.string,
  includeInstitutions: PropTypes.bool,
  includeTraining: PropTypes.bool
}

export { NextSteps }
